import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useStartManualAudit } from '@/hooks/useAmazonMutations'
import { useBrands } from '@/hooks/useBrands'
import { ImageUploadGrid, type UploadedImage } from './ImageUploadGrid'
import { BulletListEditor } from './BulletListEditor'
import { ManualAuditSettings } from './ManualAuditSettings'
import { OptionalFieldsGrid } from './OptionalFieldsGrid'

interface ManualProductFormProps {
  onOpenChange: (open: boolean) => void
  analysisMode?: 'small' | 'standard' | 'custom'
}

const INPUT_CLS = 'w-full px-3 py-2 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500'
const LABEL_CLS = 'block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1'

function BrandInput({ value, onChange, brands, label, placeholder, inputClassName, labelClassName }: {
  value: string; onChange: (v: string) => void; brands: { id: string; name: string }[]
  label: string; placeholder: string; inputClassName: string; labelClassName: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const filtered = value
    ? brands.filter((b) => b.name.toLowerCase().includes(value.toLowerCase()))
    : brands

  return (
    <div ref={ref} className="relative">
      <label className={labelClassName}>{label}</label>
      <input value={value} onChange={(e) => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder} className={inputClassName} />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-40 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg">
          {filtered.map((b) => (
            <button key={b.id} type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(b.name); setOpen(false) }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
              {b.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function ManualProductForm({ onOpenChange, analysisMode = 'standard' }: ManualProductFormProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const mutation = useStartManualAudit()
  const { data: brands } = useBrands()

  const [title, setTitle] = useState('')
  const [brand, setBrand] = useState('')
  const [bullets, setBullets] = useState([''])
  const [description, setDescription] = useState('')
  const [gtin, setGtin] = useState('')
  const [sku, setSku] = useState('')
  const [price, setPrice] = useState('')
  const [category, setCategory] = useState('')
  const [images, setImages] = useState<UploadedImage[]>([])

  const canSubmit = title.trim().length > 0 && !mutation.isPending

  const handleSubmit = () => {
    if (!canSubmit) return
    const imageAssetIds = images.filter((img) => img.assetUrl).map((img) => img.assetUrl)
    mutation.mutate(
      {
        tld: 'de', title: title.trim(), bullets: bullets.filter(Boolean), description,
        brand, image_asset_ids: imageAssetIds, report_language: i18n.language,
        mode: analysisMode,
        ...(gtin && { gtin }), ...(sku && { sku }), ...(price && { price }),
        ...(category && { category }),
      },
      { onSuccess: (data) => {
        onOpenChange(false)
        const base = `/v2/tools/product-listing-audit/${data.audit_product_id ?? data.job_id}`
        navigate(data.audit_product_id ? `${base}?tab=audit&audit_id=${data.job_id}` : base)
      } }
    )
  }

  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
      <div>
        <label className={LABEL_CLS}>{t('amazonListing.manual.productTitle')} *</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className={INPUT_CLS} />
      </div>
      <BrandInput value={brand} onChange={setBrand} brands={brands ?? []}
        label={t('amazonListing.manual.brand')} placeholder={t('amazonListing.manual.brandPlaceholder')}
        inputClassName={INPUT_CLS} labelClassName={LABEL_CLS} />
      <BulletListEditor bullets={bullets} onChange={setBullets} inputClassName={INPUT_CLS} />
      <div>
        <label className={LABEL_CLS}>{t('amazonListing.manual.description')}</label>
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className={INPUT_CLS} />
      </div>
      <OptionalFieldsGrid gtin={gtin} sku={sku} price={price} category={category}
        onGtinChange={setGtin} onSkuChange={setSku} onPriceChange={setPrice}
        onCategoryChange={setCategory} inputClassName={INPUT_CLS} />
      <ImageUploadGrid images={images} onChange={setImages} />
      <ManualAuditSettings error={mutation.error} canSubmit={canSubmit}
        isPending={mutation.isPending} onSubmit={handleSubmit} />
    </div>
  )
}
